import { useCallback, useMemo } from 'react';

import { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import { selectFilters } from '@/modules/analytics/redux/selectors';
import { selectAccountId, selectBrainId } from '@/redux/session/selectors';
import { Log, LogsQueryVariables } from '@/shared/types/analytics';
import { getAnalyticsRangeLocal } from '@/util/analytics';
import { mapFirstMessageTypeFilter } from '@/util/firstMessageTypeFilter';
import { getGraphQLSet, parseFilter, isUuid } from '@/util/util';

import { LogSessionsCount, LogSessionsPreview, SESSIONS_COUNT, SESSIONS_PREVIEW } from './useLogs';

// Shared empty array so `data` keeps a stable identity while no rows are loaded. A fresh []
// per render makes `data` an ever-changing dependency for every effect that consumes it.
const NO_ROWS: Log[] = [];

export interface BrainLogsInfiniteQueryResult {
  data: Log[];
  isLoading: boolean;
  isError: boolean;
  error?: ErrorLike;
  fetchMore: () => Promise<void>;
  totalCount: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

interface UseBrainLogsInfiniteQueryOptions {
  limit?: number;
}

/**
 * Hook for fetching brain logs with infinite scrolling support
 * Provides data fetching, pagination, and loading states for brain log sessions
 *
 * @param options - Configuration options
 * @param options.limit - Number of items to fetch per page (default: 50)
 * @returns Query result with infinite scrolling support including data, loading states, and fetch methods
 *
 * @example
 * const { data, fetchMore, hasNextPage } = useBrainLogsInfiniteQuery({ limit: 20 });
 */
export function useBrainLogsInfiniteQuery({
  limit = 50,
}: UseBrainLogsInfiniteQueryOptions = {}): BrainLogsInfiniteQueryResult {
  const accountId = useSelector(selectAccountId);
  const brainId = useSelector(selectBrainId);
  const filters = useSelector(selectFilters);

  // Build variables for brain logs
  const variables = useMemo(() => {
    return Object.assign(
      {},
      {
        accountId,
        brainIds: getGraphQLSet([brainId]),
        ...getAnalyticsRangeLocal(filters.startDate, filters?.endDate),
      },
      ...filters.logs.map((filter) => {
        if (filter.type === 'reactions') {
          return {
            min_num_thumbs_up: filter[filter.type] === true ? 1 : 0,
            min_num_thumbs_down: filter[filter.type] === false ? 1 : 0,
          };
        }
        // Map environments to deskIds for GraphQL
        if (filter.type === 'environments') {
          // Convert string array to object array format expected by parseFilter
          const deskIdsAsObjects = filter.deskIds?.map((id: string) => ({ value: id })) || [];
          return {
            deskIds: parseFilter({ ...filter, type: 'deskIds', deskIds: deskIdsAsObjects }),
          };
        }
        if (filter.type === 'firstMessageType') {
          return mapFirstMessageTypeFilter(filter.firstMessageType);
        }
        if (filter.type === 'tags') {
          return { tags: parseFilter(filter), tagsMatch: filter.tagsMode ?? 'any' };
        }
        return { [filter.type]: parseFilter(filter) };
      })
    );
  }, [accountId, brainId, filters]);

  // Fetch total count
  const { data: countData } = useQuery<LogSessionsCount, LogsQueryVariables>(SESSIONS_COUNT, {
    variables,
    skip: !accountId || !isUuid(brainId) || !filters.filtersLoaded,
    // Count is non-critical — pagination still works without it
    errorPolicy: 'ignore',
  });

  const totalCount = countData?.rows?.[0]?.total_count ?? 0;

  // Fetch paginated data
  const {
    data: previewData,
    loading,
    error,
    fetchMore: apolloFetchMore,
    networkStatus,
  } = useQuery<LogSessionsPreview, LogsQueryVariables>(SESSIONS_PREVIEW, {
    variables: { ...variables, limit, offset: 0 },
    skip: !accountId || !isUuid(brainId) || !filters.filtersLoaded,
    notifyOnNetworkStatusChange: true,
  });

  const data = previewData?.rows || NO_ROWS;
  const hasNextPage = data.length < totalCount;
  const isFetchingNextPage = networkStatus === 3; // NetworkStatus.fetchMore

  const handleFetchMore = useCallback(async () => {
    if (!hasNextPage || loading || isFetchingNextPage) {
      return;
    }

    try {
      await apolloFetchMore({
        variables: { ...variables, limit, offset: data.length },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult || !fetchMoreResult.rows.length) {
            return prev;
          }
          return { ...prev, rows: [...prev.rows, ...fetchMoreResult.rows] };
        },
      });
    } catch (err) {
      console.error('Error fetching more sessions:', err);
    }
  }, [apolloFetchMore, variables, limit, data.length, hasNextPage, loading, isFetchingNextPage]);

  return {
    data,
    isLoading: loading && !data.length,
    isError: !!error,
    error,
    fetchMore: handleFetchMore,
    totalCount,
    hasNextPage,
    isFetchingNextPage,
  };
}
